/**
 * The one definition of a postal address rendered as a single line.
 *
 * A location's address is one question, and it was being answered in four
 * places with three different answers. `formatFleetAddress` (product detail)
 * had the right one; the Zugangsverwaltung card, the station detail header and
 * the location detail page each built `street, postcode city` and **dropped the
 * house number entirely** — "Teststraße, 04103 Leipzig" for an address entered
 * as Teststraße 7 (QA #2439, defect D). The rule lives here now and those
 * surfaces import it.
 *
 * The house number is appended only when the street does not already end in it.
 * Tenant data carries it BOTH ways — `AddressSection` has separate fields, but
 * operators also type "Paulinerstr. 8" into the street and "8" beside it, which
 * rendered as "Paulinerstr. 8 8" (staging, PO 2026-08-31). The backend's own
 * `formatAddress` sidesteps this by ignoring `house_number` entirely; that
 * would drop the number from correctly-split records, so this de-duplicates
 * instead of choosing one field.
 *
 * `houseNumber` is on the wire and always has been: the address is read without
 * a `select` (`locationService.getAllLocations` / `getAllByType` →
 * `addressService.findMany`) and `processPGData` camel-cases every column, so
 * `house_number` arrives as `houseNumber`. Only the admin-ui types and these
 * three formatters had never named it.
 *
 * THE COPY THAT CANNOT IMPORT THIS: `apps/end-user/src/util/locationAddress.ts`
 * — a separate deployable, answering the same question for the customer. Change
 * the rule here and change it there, or the same station reads differently in
 * the admin and in the app.
 */

export interface AddressLineParts {
    street?: string | null;
    houseNumber?: string | null;
    postalCode?: string | null;
    city?: string | null;
}

const trimmed = (value: string | null | undefined): string =>
    typeof value === 'string' ? value.trim() : '';

/** "Paulinerstr. 8, 37073 Göttingen", or '' when nothing is known. */
export const formatAddressLine = (address: AddressLineParts | null | undefined): string => {
    if (!address) return '';
    const streetName = trimmed(address.street);
    const houseNumber = trimmed(address.houseNumber);
    const alreadyInStreet = houseNumber.length > 0
        && new RegExp(`(^|\\s)${houseNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`).test(streetName);
    const street = [streetName, alreadyInStreet ? '' : houseNumber]
        .filter((part) => part.length > 0)
        .join(' ');
    const town = [trimmed(address.postalCode), trimmed(address.city)]
        .filter((part) => part.length > 0)
        .join(' ');
    // Never a half-built string: a missing street used to leave a stray leading
    // comma on screen (#2433).
    return [street, town].filter((part) => part.length > 0).join(', ');
};
